/* NEO — PDP mobile cart bar drawer
 * Keeps the original cart-bar.js behavior intact on desktop.
 * Moves (never clones) the existing product details on mobile and restores them on desktop.
 */

(() => {
  'use strict';

  const neo_mobile_query = window.matchMedia('(max-width: 767px)');
  const neo_instances = new WeakMap();

  class NeoMobileCartBarDrawer {
    constructor(neo_product_info) {
      this.neo_product_info = neo_product_info;
      this.neo_details = neo_product_info.querySelector('.product-single__details');
      this.neo_cart_bar = neo_product_info.querySelector('cart-bar');
      this.neo_destination = neo_product_info.querySelector('[data-neo-cart-bar-destination]');
      this.neo_content = neo_product_info.querySelector('[data-neo-cart-bar-content]');
      this.neo_handle = neo_product_info.querySelector('[data-neo-cart-bar-handle]');
      this.neo_expand_button = neo_product_info.querySelector('[data-neo-cart-bar-expand]');
      this.neo_cart_bar_wrapper = this.neo_cart_bar?.querySelector(':scope > [data-form-wrapper]');
      this.neo_cart_bar_form = this.neo_cart_bar?.querySelector('.cart-bar__form');
      this.neo_origin = document.createComment('NEO: product details original position');
      this.neo_destination_origin = document.createComment('NEO: cart bar option wrap mobile position');
      this.neo_pointer_start_y = null;
      this.neo_footer_observer = null;
      this.neo_ellipsis_refresh_frame = null;

      if (!this.neo_details || !this.neo_cart_bar || !this.neo_destination || !this.neo_content || !this.neo_handle || !this.neo_expand_button || !this.neo_cart_bar_form) {
        return;
      }

      this.neo_details.before(this.neo_origin);
      this.neo_destination.before(this.neo_destination_origin);
      this.neo_on_media_change = () => this.neo_sync_layout();
      this.neo_on_handle_click = () => this.neo_toggle();
      this.neo_on_expand_click = () => {
        if (neo_mobile_query.matches) {
          this.neo_set_expanded(true);
          return;
        }

        this.neo_scroll_to_product_options();
      };
      this.neo_on_pointer_down = (neo_event) => {
        this.neo_pointer_start_y = neo_event.clientY;
      };
      this.neo_on_pointer_up = (neo_event) => this.neo_finish_drag(neo_event);

      this.neo_handle.addEventListener('click', this.neo_on_handle_click);
      this.neo_expand_button.addEventListener('click', this.neo_on_expand_click);
      this.neo_handle.addEventListener('pointerdown', this.neo_on_pointer_down);
      this.neo_handle.addEventListener('pointerup', this.neo_on_pointer_up);
      neo_mobile_query.addEventListener('change', this.neo_on_media_change);

      this.neo_observe_footer();
      this.neo_sync_layout();
    }

    neo_sync_layout() {
      if (neo_mobile_query.matches) {
        this.neo_restore_destination();

        if (this.neo_details.parentNode !== this.neo_content) {
          this.neo_content.append(this.neo_details);
        }

        // NEO 2026.08.26 — the original AOS anchor may already be above the viewport
        // when a mobile page is reloaded mid-scroll. Reveal only the content moved
        // into this fixed drawer so it cannot remain at the AOS hidden opacity.
        this.neo_reveal_moved_content();

        // NEO: keep later product-info queries on the real product form.
        this.neo_cart_bar_wrapper?.removeAttribute('data-form-wrapper');
        this.neo_cart_bar.classList.add('neo-cart-bar--mobile-visible');
        return;
      }

      this.neo_restore_details();
      // NEO 2026.08.27 — desktop keeps the original PDP details in place and
      // moves only the custom option UI into the existing cart bar form.
      if (this.neo_destination.parentNode !== this.neo_cart_bar_form) {
        this.neo_cart_bar_form.prepend(this.neo_destination);
      }
      this.neo_set_expanded(false);
      this.neo_cart_bar.classList.remove(
        'neo-cart-bar--mobile-visible',
        'neo-cart-bar--footer-visible'
      );
      this.neo_cart_bar_wrapper?.setAttribute('data-form-wrapper', '');
    }

    neo_toggle() {
      this.neo_set_expanded(!this.neo_destination.classList.contains('is-expanded'));
    }

    neo_reveal_moved_content() {
      this.neo_details.querySelectorAll('[data-aos]').forEach((neo_element) => {
        neo_element.classList.add('aos-animate');
      });
    }

    neo_scroll_to_product_options() {
      // NEO 2026.08.27 — desktop uses the CTA as a shortcut to the original
      // OPTIS block. Fall back to the main product form when that app block
      // is not present in the current product template.
      const neo_options =
        this.neo_product_info.querySelector('[data-neo-product-options]') ||
        this.neo_product_info.querySelector('product-form[data-form-wrapper]');

      if (!neo_options) return;

      neo_options.setAttribute('tabindex', '-1');
      neo_options.focus({ preventScroll: true });
      neo_options.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    neo_set_expanded(neo_expanded) {
      this.neo_destination.classList.toggle('is-expanded', neo_expanded);
      this.neo_handle.setAttribute('aria-expanded', String(neo_expanded));

      if (this.neo_ellipsis_refresh_frame !== null) {
        window.cancelAnimationFrame(this.neo_ellipsis_refresh_frame);
        this.neo_ellipsis_refresh_frame = null;
      }

      if (neo_expanded) {
        this.neo_reveal_moved_content();

        // NEO 2026.09.03 — toggle-ellipsis can initialize at 0px while its
        // product block is hidden in the collapsed cart bar. Recalculate it
        // after the drawer content becomes visible.
        this.neo_ellipsis_refresh_frame = window.requestAnimationFrame(() => {
          this.neo_refresh_accordion_heights();
          this.neo_ellipsis_refresh_frame = null;
        });
      }

      if (!neo_expanded) {
        this.neo_content.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    neo_refresh_accordion_heights() {
      this.neo_content.querySelectorAll('toggle-ellipsis').forEach((neo_ellipsis) => {
        const neo_ellipsis_content = neo_ellipsis.querySelector('[data-content]');
        const neo_ellipsis_actions = neo_ellipsis.querySelector('[data-actions]');
        const neo_initial_height = Number(neo_ellipsis.dataset.height);

        if (!neo_ellipsis_content) return;

        const neo_content_height = neo_ellipsis_content.scrollHeight;
        const neo_has_initial_height = Number.isFinite(neo_initial_height) && neo_initial_height > 0;
        const neo_collapsed_height = neo_has_initial_height
          ? Math.min(neo_content_height, neo_initial_height)
          : neo_content_height;
        const neo_needs_toggle = neo_has_initial_height && neo_content_height > neo_initial_height;

        neo_ellipsis.classList.toggle('is-enabled', neo_needs_toggle);

        const neo_actions_height = neo_ellipsis_actions?.offsetHeight || 0;
        const neo_target_height = neo_ellipsis.classList.contains('is-open')
          ? neo_content_height + neo_actions_height * 2
          : neo_collapsed_height;

        neo_ellipsis.style.setProperty('--height', `${neo_target_height}px`);
      });

      this.neo_content
        .querySelectorAll('[data-collapsible-container].is-expanded')
        .forEach((neo_accordion_content) => {
          const neo_accordion_inner = neo_accordion_content.querySelector(
            '[data-collapsible-content]'
          );

          if (neo_accordion_inner) {
            neo_accordion_content.style.height = `${neo_accordion_inner.scrollHeight}px`;
          }
        });
    }

    neo_finish_drag(neo_event) {
      if (this.neo_pointer_start_y === null) return;

      const neo_distance = neo_event.clientY - this.neo_pointer_start_y;
      this.neo_pointer_start_y = null;

      if (Math.abs(neo_distance) < 40) return;

      this.neo_set_expanded(neo_distance < 0);
      // NEO: a completed swipe must not also run the following click toggle.
      this.neo_handle.removeEventListener('click', this.neo_on_handle_click);
      window.setTimeout(() => {
        this.neo_handle.addEventListener('click', this.neo_on_handle_click);
      }, 0);
    }

    neo_observe_footer() {
      const neo_footer = document.querySelector(
        '.shopify-section-group-footer-group, [data-section-type="footer"], footer'
      );

      if (!neo_footer) return;

      this.neo_footer_observer = new IntersectionObserver((neo_entries) => {
        const neo_footer_visible = neo_entries.some((neo_entry) => neo_entry.isIntersecting);
        this.neo_cart_bar.classList.toggle('neo-cart-bar--footer-visible', neo_footer_visible);

        if (neo_footer_visible) this.neo_set_expanded(false);
      });
      this.neo_footer_observer.observe(neo_footer);
    }

    neo_restore_details() {
      if (this.neo_origin.parentNode && this.neo_details.parentNode !== this.neo_origin.parentNode) {
        this.neo_origin.parentNode.insertBefore(this.neo_details, this.neo_origin.nextSibling);
      }
    }

    neo_restore_destination() {
      if (
        this.neo_destination_origin.parentNode &&
        this.neo_destination.parentNode !== this.neo_destination_origin.parentNode
      ) {
        this.neo_destination_origin.parentNode.insertBefore(
          this.neo_destination,
          this.neo_destination_origin.nextSibling
        );
      }
    }

    destroy() {
      this.neo_restore_details();
      this.neo_restore_destination();
      this.neo_footer_observer?.disconnect();
      if (this.neo_ellipsis_refresh_frame !== null) {
        window.cancelAnimationFrame(this.neo_ellipsis_refresh_frame);
      }
      this.neo_handle.removeEventListener('click', this.neo_on_handle_click);
      this.neo_expand_button.removeEventListener('click', this.neo_on_expand_click);
      this.neo_handle.removeEventListener('pointerdown', this.neo_on_pointer_down);
      this.neo_handle.removeEventListener('pointerup', this.neo_on_pointer_up);
      neo_mobile_query.removeEventListener('change', this.neo_on_media_change);
      this.neo_cart_bar_wrapper?.setAttribute('data-form-wrapper', '');
      this.neo_origin.remove();
      this.neo_destination_origin.remove();
    }
  }

  const neo_init_mobile_cart_bar_drawers = (neo_root = document) => {
    neo_root.querySelectorAll('product-info[data-neo-mobile-cart-bar-drawer]').forEach((neo_product_info) => {
      if (neo_instances.has(neo_product_info)) return;
      neo_instances.set(neo_product_info, new NeoMobileCartBarDrawer(neo_product_info));
    });
  };

  document.addEventListener('DOMContentLoaded', () => neo_init_mobile_cart_bar_drawers());
  document.addEventListener('shopify:section:load', (neo_event) => {
    neo_init_mobile_cart_bar_drawers(neo_event.target);
  });
  document.addEventListener('shopify:section:unload', (neo_event) => {
    neo_event.target
      .querySelectorAll('product-info[data-neo-mobile-cart-bar-drawer]')
      .forEach((neo_product_info) => {
        neo_instances.get(neo_product_info)?.destroy();
        neo_instances.delete(neo_product_info);
      });
  });
})();
